import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Download,
  FileText,
  Globe,
  Menu,
  X,
  MapPin,
  Mail,
  Star
} from 'lucide-react';
import { 
  GiDolphin,
  GiTurtle,
  GiIsland,
  GiSparkles,
  GiDesert,
  GiEgyptianPyramids,
  GiTeacher,
  GiCheckedShield,
  GiCoins,
  GiBeveledStar
} from 'react-icons/gi';
import { 
  FaInstagram,
  FaFacebook,
  FaWhatsapp
} from 'react-icons/fa';
import { 
  translations, 
  galleryImages, 
  catalogPdfUrl, 
  googleFormUrl,
  whatsappNumber,
  instagramHandle,
  facebookGroupUrl
} from '../mock';

const iconMap = {
  dolphin: GiDolphin,
  turtle: GiTurtle,
  island: GiIsland,
  magic: GiSparkles,
  desert: GiDesert,
  pyramid: GiEgyptianPyramids
};

const languageOptions = [
  { code: 'it', label: 'Italiano', short: 'IT' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'pl', label: 'Polski', short: 'PL' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'de', label: 'Deutsch', short: 'DE' }
];

// Flag components as simple colored SVGs
const FlagIcon = ({ code }) => {
  const flags = {
    it: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 30 20">
        <rect width="10" height="20" fill="#009246"/>
        <rect x="10" width="10" height="20" fill="#fff"/>
        <rect x="20" width="10" height="20" fill="#ce2b37"/>
      </svg>
    ),
    en: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 60 30">
        <rect width="60" height="30" fill="#012169"/>
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4"/>
        <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10"/>
        <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6"/>
      </svg>
    ),
    pl: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 30 20">
        <rect width="30" height="10" fill="#fff"/>
        <rect y="10" width="30" height="10" fill="#dc143c"/>
      </svg>
    ),
    es: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 30 20">
        <rect width="30" height="5" fill="#c60b1e"/>
        <rect y="5" width="30" height="10" fill="#ffc400"/>
        <rect y="15" width="30" height="5" fill="#c60b1e"/>
      </svg>
    ),
    fr: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 30 20">
        <rect width="10" height="20" fill="#002395"/>
        <rect x="10" width="10" height="20" fill="#fff"/>
        <rect x="20" width="10" height="20" fill="#ed2939"/>
      </svg>
    ),
    de: (
      <svg className="w-5 h-4 rounded-sm overflow-hidden" viewBox="0 0 30 20">
        <rect width="30" height="7" fill="#000"/>
        <rect y="7" width="30" height="6" fill="#dd0000"/>
        <rect y="13" width="30" height="7" fill="#ffcc00"/>
      </svg>
    )
  };
  return flags[code] || null;
};

export const LandingPage = () => {
  const [language, setLanguage] = useState('it');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const t = translations[language];

  useEffect(() => {
    // Removed scroll listener
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 200; // Offset per l'header fisso
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Title - Centered */}
          <div className="flex items-center justify-center py-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_15109e7d-7c63-4edb-9455-d202e36ce359/artifacts/7z0etq9l_Logo.jpg" 
                alt="Dream Tour Logo" 
                className="h-32 w-32 object-contain rounded-full"
              />
              <div className="text-center">
                <h1 className="font-bold text-2xl text-ocean-600">DREAM TOUR MARSA ALAM</h1>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-center space-x-8 py-4">
            <button onClick={() => scrollToSection('home')} className="nav-link">{t.nav.home}</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">{t.nav.about}</button>
            <button onClick={() => scrollToSection('tours')} className="nav-link">{t.nav.tours}</button>
            <button onClick={() => scrollToSection('gallery')} className="nav-link">{t.nav.gallery}</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link">{t.nav.testimonials}</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">{t.nav.contact}</button>
            
            <button 
              onClick={() => window.open(googleFormUrl, '_blank')}
              className="nav-link quote-link"
            >
              {t.nav.quote}
            </button>
            
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center space-x-2"
              >
                <FlagIcon code={language} />
                <span>{languageOptions.find(l => l.code === language)?.short}</span>
              </Button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-ocean-50 flex items-center space-x-3 ${language === lang.code ? 'bg-ocean-100 text-ocean-700 font-semibold' : 'text-gray-700'}`}
                    >
                      <FlagIcon code={lang.code} />
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center justify-between py-3">
            <button
              className="text-ocean-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            
            <button 
              onClick={() => window.open(googleFormUrl, '_blank')}
              className="bg-coral-500 hover:bg-coral-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            >
              {t.nav.quote}
            </button>
            
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center space-x-2"
              >
                <FlagIcon code={language} />
                <span>{languageOptions.find(l => l.code === language)?.short}</span>
              </Button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-ocean-50 flex items-center space-x-3 ${language === lang.code ? 'bg-ocean-100 text-ocean-700 font-semibold' : 'text-gray-700'}`}
                    >
                      <FlagIcon code={lang.code} />
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 bg-white/98 backdrop-blur-md rounded-b-lg shadow-xl">
              <button onClick={() => scrollToSection('home')} className="mobile-nav-link">{t.nav.home}</button>
              <button onClick={() => scrollToSection('about')} className="mobile-nav-link">{t.nav.about}</button>
              <button onClick={() => scrollToSection('tours')} className="mobile-nav-link">{t.nav.tours}</button>
              <button onClick={() => scrollToSection('gallery')} className="mobile-nav-link">{t.nav.gallery}</button>
              <button onClick={() => scrollToSection('testimonials')} className="mobile-nav-link">{t.nav.testimonials}</button>
              <button onClick={() => scrollToSection('contact')} className="mobile-nav-link">{t.nav.contact}</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="hero-video"
        >
          <source src="https://customer-assets.emergentagent.com/job_sea-turtle-tours/artifacts/neh6i9z9_Underwater_Scene_Generation_Complete.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay" />
        <div className="container mx-auto px-4 h-full flex items-end pb-24">
          <div className="hero-content max-w-3xl">
            <h1 className="hero-title">
              {t.hero.title}
            </h1>
            <p className="hero-subtitle">
              {t.hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2 className="section-title">{t.about.title}</h2>
            <p className="section-subtitle">{t.about.subtitle}</p>
          </div>

          <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center mb-16 leading-relaxed">
            {t.about.description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.about.features.map((feature, index) => {
              const icons = [GiTeacher, GiCheckedShield, GiCoins, GiBeveledStar];
              const Icon = icons[index];
              return (
                <div key={index} className="feature-card">
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mb-4 mx-auto feature-icon">
                    <Icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3 text-center">{feature.title}</h4>
                  <p className="text-gray-600 text-center">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section id="tours" className="section-padding">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2 className="section-title">{t.tours.title}</h2>
            <p className="section-subtitle">{t.tours.subtitle}</p>
            <p 
              className="text-xl md:text-2xl mt-4 font-bold tracking-wide cursor-pointer transition-all hover:opacity-80"
              style={{ color: '#f97316', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
              onClick={() => window.open(catalogPdfUrl, '_blank')}
            >
              {t.tours.clickInfo}
            </p>
          </div>

          {/* Sea Tours */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-ocean-700 mb-8 text-center">
              {t.tours.seaTours.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.tours.seaTours.tours.map((tour, index) => {
                const Icon = iconMap[tour.icon];
                return (
                  <Card 
                    key={index} 
                    className="tour-card cursor-pointer"
                    onClick={() => window.open(catalogPdfUrl, '_blank')}
                  >
                    <CardHeader>
                      <div className="w-16 h-16 rounded-full bg-ocean-100 flex items-center justify-center mb-4 tour-icon">
                        <Icon className="h-8 w-8 text-ocean-600" />
                      </div>
                      <CardTitle className="text-xl">{tour.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{tour.description}</CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Desert Tours */}
          <div className="mb-12">
            <h3 className="text-3xl font-bold text-amber-700 mb-8 text-center">
              {t.tours.desertTours.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {t.tours.desertTours.tours.map((tour, index) => {
                const Icon = iconMap[tour.icon];
                return (
                  <Card 
                    key={index} 
                    className="tour-card cursor-pointer"
                    onClick={() => window.open(catalogPdfUrl, '_blank')}
                  >
                    <CardHeader>
                      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4 tour-icon">
                        <Icon className="h-8 w-8 text-amber-600" />
                      </div>
                      <CardTitle className="text-xl">{tour.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{tour.description}</CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-padding">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2 className="section-title">{t.gallery.title}</h2>
            <p className="section-subtitle">{t.gallery.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.map((image, index) => (
              <div key={index} className="gallery-item">
                <img 
                  src={image} 
                  alt={`Gallery ${index + 1}`} 
                  className="w-full h-80 object-cover rounded-xl"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  draggable="false"
                />
                <div className="gallery-watermark">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_sea-turtle-tours/artifacts/y43dcjrt_Logo.jpg" 
                    alt="Dream Tour Watermark"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    draggable="false"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2 className="section-title">{t.testimonials.title}</h2>
            <p className="section-subtitle">{t.testimonials.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.testimonials.reviews.map((review, index) => (
              <Card key={index} className="testimonial-card">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{review.name}</CardTitle>
                  <CardDescription className="flex items-center text-sm">
                    <MapPin className="h-4 w-4 mr-1" />
                    {review.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic">"{review.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2 className="section-title">{t.contact.title}</h2>
            <p className="section-subtitle">{t.contact.subtitle}</p>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            {/* WhatsApp */}
            <Card className="contact-info-card">
              <CardContent className="pt-6">
                <Button
                  onClick={handleWhatsApp}
                  className="w-full btn-whatsapp-large"
                  size="lg"
                >
                  <FaWhatsapp className="mr-3 h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">{t.contact.info.whatsapp}</div>
                    <div className="text-sm opacity-90">{whatsappNumber}</div>
                  </div>
                </Button>
              </CardContent>
            </Card>

            {/* Instagram */}
            <Card className="contact-info-card">
              <CardContent className="pt-6">
                <Button
                  onClick={() => window.open(`https://instagram.com/${instagramHandle}`, '_blank')}
                  className="w-full btn-instagram"
                  size="lg"
                  variant="outline"
                >
                  <FaInstagram className="mr-3 h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">{t.contact.info.instagram}</div>
                    <div className="text-sm">@{instagramHandle}</div>
                  </div>
                </Button>
              </CardContent>
            </Card>

            {/* Facebook */}
            <Card className="contact-info-card">
              <CardContent className="pt-6">
                <Button
                  onClick={() => window.open(facebookGroupUrl, '_blank')}
                  className="w-full btn-facebook"
                  size="lg"
                  variant="outline"
                >
                  <FaFacebook className="mr-3 h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">{t.contact.info.facebook}</div>
                    <div className="text-sm">ITALIANI A MARSA ALAM</div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ocean-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_15109e7d-7c63-4edb-9455-d202e36ce359/artifacts/7z0etq9l_Logo.jpg" 
                  alt="Dream Tour Logo" 
                  className="h-12 w-12 object-contain rounded-full"
                />
                <span className="font-bold text-xl">DREAM TOUR</span>
              </div>
              <p className="text-ocean-200">MARSA ALAM</p>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">{t.footer.followUs}</h4>
              <div className="flex space-x-4">
                <button 
                  onClick={() => window.open(`https://instagram.com/${instagramHandle}`, '_blank')}
                  className="social-icon"
                >
                  <FaInstagram className="h-6 w-6" />
                </button>
                <button 
                  onClick={() => window.open(facebookGroupUrl, '_blank')}
                  className="social-icon"
                >
                  <FaFacebook className="h-6 w-6" />
                </button>
                <button 
                  onClick={handleWhatsApp}
                  className="social-icon"
                >
                  <FaWhatsapp className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-4">{t.nav.contact}</h4>
              <div className="space-y-2 text-ocean-200">
                <p className="flex items-center">
                  <FaWhatsapp className="h-4 w-4 mr-2" />
                  {whatsappNumber}
                </p>
                <p className="flex items-center">
                  <FaInstagram className="h-4 w-4 mr-2" />
                  @{instagramHandle}
                </p>
                <p className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  info@dream-tour.it
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8 bg-ocean-700" />

          <div className="text-center text-ocean-300 text-sm">
            <p>© {new Date().getFullYear()} DREAM TOUR MARSA ALAM. {t.footer.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};